import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Search, CalendarDays, Download, CheckCircle, Circle, Anchor } from 'lucide-react';
import { createPDF, addTable } from '../lib/pdf';
import { toast } from 'sonner';
import api, { getCourses, exportCourses, getMonitorCourses, updateReservationAttendees, updateCourse, createCourse } from '../lib/api';
import TideChart, { getSpainOffset } from '../components/TideChart';

const LOCATIONS = { san_antolin: 'San Antolín', san_martin: 'San Martín', ria_niembro: 'Ría de Niembro' };
const getLoc = (v) => LOCATIONS[v] || v;
const fmtCoef = (c) => c == null ? '' : Math.round(c < 2 ? c * 100 : c);
const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : d; };
const COURSE_COLORS = ['#6A4C93', '#1982C4', '#F2CC8F', '#FF595E', '#8AC926', '#FF6B35', '#00B4D8', '#E36414'];

// CALENDARIO helpers — same logic as PDF generator, no side-effects
const CAL_UTC = 2;
const calRound = (h, m) => m < 15 ? [h, 0] : m < 45 ? [h, 30] : [(h + 1) % 24, 0];
const calFmt = (h, m) => `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
function calClassTime(utc) {
  const [hh, mm] = utc.split(':').map(Number);
  const tot = hh * 60 + mm + CAL_UTC * 60 + 120;
  const [rh, rm] = calRound(Math.floor(tot / 60) % 24, tot % 60);
  let t = calFmt(rh, rm);
  if (t === '09:30') t = '10:00'; if (t === '18:30') t = '18:00'; return t;
}
function calSupTime(utc) {
  const [hh, mm] = utc.split(':').map(Number);
  const lt = hh * 60 + mm + CAL_UTC * 60;
  const lh = Math.floor(lt / 60) % 24, lm = lt % 60;
  if (lh * 60 + lm >= 540 && lh * 60 + lm <= 630) { const [rh, rm] = calRound(lh, lm); return calFmt(rh, rm); }
  const tot = hh * 60 + mm + CAL_UTC * 60 - 60;
  const [rh, rm] = calRound(Math.floor(((tot % 1440) + 1440) / 60) % 24, ((tot % 60) + 60) % 60);
  let s = calFmt(rh, rm); if (s === '19:00') s = '18:30'; if (s === '18:30') s = '18:00'; return s;
}
const calLocal = (utc) => { const [hh, mm] = utc.split(':').map(Number); const t = hh * 60 + mm + CAL_UTC * 60; return calFmt(Math.floor(t / 60) % 24, t % 60); };
const calMin = (t) => { const [h, m] = (t || '00:00').split(':').map(Number); return h * 60 + m; };
const calIsPractical = (t) => { const m = calMin(t); return m >= 480 && m <= 1140; };
const calClassColor = (t) => { const m = calMin(t); if (m >= 810 && m <= 930) return 'yellow'; if (m >= 600 && m <= 1080) return 'green'; return 'red'; };
const calSupColor  = (t) => { const m = calMin(t); if (m >= 810 && m <= 930) return 'yellow'; if (m >= 600 && m <= 1140) return 'green'; return 'red'; };
const CAL_COLOR = { green: '#2E6B4F', yellow: '#7A5C00', red: '#9A2A2A' };
const CAL_DAYS = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
const CAL_MONTHS = { '06': 'JUNIO', '07': 'JULIO', '08': 'AGOSTO', '09': 'SEPTIEMBRE' };

function BulkAttendeeRow({ reservation }) {
  const [value, setValue] = useState(reservation.actual_attendees ?? '');
  const [saving, setSaving] = useState(false);
  const max = reservation.num_students || 0;

  const handleSave = async (v) => {
    const num = parseInt(v);
    if (isNaN(num) || num < 0) return;
    const clamped = Math.min(num, max);
    setValue(clamped);
    setSaving(true);
    try {
      await updateReservationAttendees(reservation.id, clamped);
    } catch { /* silently fail */ }
    finally { setSaving(false); }
  };

  return (
    <div className="flex items-center justify-between py-1 bg-[#F5F2ED] rounded px-2">
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-[#1A2B3C] truncate block">{reservation.reservation_name}</span>
        <span className="text-[10px] text-[#8A9BA8]">{max} alumnos reservados</span>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-[10px] text-[#8A9BA8]">Asistentes:</span>
        <input
          type="number" min="0" max={max}
          value={value} onChange={e => setValue(e.target.value)}
          onBlur={e => handleSave(e.target.value)}
          className={`w-12 h-7 text-center text-sm border rounded ${saving ? 'border-[#81B29A] bg-[#F0FFF4]' : 'border-[#E5E0D8] bg-white'}`}
          data-testid={`bulk-attendees-${reservation.id}`}
        />
        <span className="text-[10px] text-[#8A9BA8]">/ {max}</span>
      </div>
    </div>
  );
}


export default function SchedulePage({ monitorFilter }) {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [tideData, setTideData] = useState({});
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [calendarDate, setCalendarDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [tab, setTab] = useState('proximos');
  const [calSaving, setCalSaving] = useState({});
  const [calEditValues, setCalEditValues] = useState({});
  const [marineByDate, setMarineByDate] = useState({});
  const [windByDate, setWindByDate] = useState({});

  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const coursesPromise = monitorFilter ? getMonitorCourses() : getCourses();
      const [coursesRes, tidesRes, attRes] = await Promise.all([
        coursesPromise,
        api.getTides(),
        api.getAttendance(),
      ]);
      setCourses(coursesRes.data);
      setTideData(tidesRes.data);
      const attMap = {};
      (attRes.data || []).forEach(a => { attMap[`${a.course_id}_${a.student_name}`] = a.attended; });
      setAttendance(attMap);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [monitorFilter]);
  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    (async () => {
      try {
        const [mRes, wRes] = await Promise.all([
          fetch('https://marine-api.open-meteo.com/v1/marine?latitude=43.43&longitude=-4.77&hourly=wave_height,wave_direction,wave_period&timezone=Europe%2FMadrid&forecast_days=5'),
          fetch('https://api.open-meteo.com/v1/forecast?latitude=43.43&longitude=-4.77&hourly=wind_speed_10m,wind_direction_10m,wind_gusts_10m&timezone=Europe%2FMadrid&forecast_days=5'),
        ]);
        const m = await mRes.json();
        const w = await wRes.json();
        const mm = {}, wm = {};
        m.hourly.time.forEach((t, i) => {
          const date = t.slice(0, 10), hour = parseInt(t.slice(11, 13));
          if (hour === 10) mm[date] = { height: m.hourly.wave_height[i], direction: m.hourly.wave_direction[i], period: Math.round(m.hourly.wave_period[i]) };
        });
        w.hourly.time.forEach((t, i) => {
          const date = t.slice(0, 10), hour = parseInt(t.slice(11, 13));
          if (hour === 10) wm[date] = { speed: w.hourly.wind_speed_10m[i], direction: w.hourly.wind_direction_10m[i], gusts: w.hourly.wind_gusts_10m[i] };
        });
        setMarineByDate(mm);
        setWindByDate(wm);
      } catch (e) { console.error('Marine API:', e); }
    })();
  }, []);

  const toggleAttendance = async (courseId, studentName) => {
    const key = `${courseId}_${studentName}`;
    const current = !!attendance[key];
    try {
      await api.toggleAttendance({ course_id: courseId, student_name: studentName, attended: !current });
      setAttendance(prev => ({ ...prev, [key]: !current }));
    } catch { toast.error('Error al guardar asistencia'); }
  };

  const filtered = useMemo(() => {
    return courses.filter(c => {
      if (monitorFilter) {
        const hasMonitor = (c.monitors || []).some(m => m.name && m.name.toLowerCase() === monitorFilter.toLowerCase());
        if (!hasMonitor) return false;
      }
      if (calendarDate) { const d = `${calendarDate.getFullYear()}-${String(calendarDate.getMonth()+1).padStart(2,"0")}-${String(calendarDate.getDate()).padStart(2,"0")}`; if (c.date !== d) return false; }
      if (!search) return true;
      const s = search.toLowerCase();
      const cMatch = [c.date, c.time, c.location, getLoc(c.location), ...(c.monitors || []).map(m => m.name)].some(f => f && f.toLowerCase().includes(s));
      const rMatch = (c.reservations || []).some(r => [r.reservation_name, r.phone, r.email, r.dni, ...(r.students || []).map(st => st.name)].some(f => f && f.toLowerCase().includes(s)));
      return cMatch || rMatch;
    });
  }, [courses, calendarDate, search, monitorFilter]);

  const grouped = useMemo(() => {
    return filtered.reduce((acc, c) => { (acc[c.date] = acc[c.date] || []).push(c); return acc; }, {});
  }, [filtered]);

  const futureDates = useMemo(() => {
    return Object.keys(grouped).filter(d => d >= todayStr).sort((a, b) => a.localeCompare(b));
  }, [grouped, todayStr]);

  const pastDates = useMemo(() => {
    return Object.keys(grouped).filter(d => d < todayStr).sort((a, b) => b.localeCompare(a));
  }, [grouped, todayStr]);

  const displayDates = tab === 'proximos' ? futureDates : pastDates;

  const handleExportExcel = async () => {
    try { const { data } = await exportCourses(); const url = URL.createObjectURL(data); const a = document.createElement('a'); a.href = url; a.download = 'programacion.xlsx'; a.click(); } catch { toast.error('Error'); }
  };

  const handleExportPDF = () => {
    const doc = createPDF();
    doc.setFontSize(14); doc.text('Programación - SURF SKULL S.L.', 14, 18);
    const rows = [];
    filtered.forEach(c => {
      const mons = (c.monitors || []).map(m => m.name).join(', ');
      (c.reservations || []).forEach(r => {
        (r.students || [{ name: r.reservation_name }]).forEach(st => {
          const key = `${c.id}_${st.name}`;
          rows.push([fmtDate(c.date), c.time, getLoc(c.location), mons, st.name, attendance[key] ? 'Sí' : 'No']);
        });
      });
      if (!c.reservations?.length) rows.push([fmtDate(c.date), c.time, getLoc(c.location), mons, '-', '-']);
    });
    addTable(doc, { startY: 25, head: [['Fecha', 'Hora', 'Ubicación', 'Monitores', 'Alumno', 'Asistencia']], body: rows, theme: 'grid', headStyles: { fillColor: [74, 109, 124] } });
    doc.save('programacion.pdf');
  };

  const handleExportCalendarExcel = () => {
    const wb = XLSX.utils.book_new();
    const wsData = [];

    wsData.push(['CALENDARIO SURF SKULL 2026', '', '', '', '', '', '', '']);
    wsData.push(['Día', 'Coef', 'Mareas', 'Clases sugeridas', 'SUP sugerido', 'S. Antolín', 'Ría Niembro', 'S. Martín']);

    ['06','07','08','09'].filter(mo => mo >= todayStr.substring(5,7)).forEach(mo => {
      const days = Object.keys(tideData)
        .filter(d => d >= todayStr && d <= '2026-09-30' && d.startsWith('2026-' + mo))
        .sort();
      if (!days.length) return;
      wsData.push([CAL_MONTHS[mo] + ' 2026', '', '', '', '', '', '', '']);
      days.forEach(dateStr => {
        const dt = new Date(dateStr + 'T12:00:00Z');
        const tide = tideData[dateStr];
        const tidesList = tide?.tides || [];
        const coef = tide?.coefficient != null ? Math.round(tide.coefficient < 2 ? tide.coefficient * 100 : tide.coefficient) : '';
        const tidesText = tidesList.map(t => `${calLocal(t.time)}${t.type==='pleamar'?'↑':'↓'}${t.height}m`).join('  ');
        const classText = tidesList.map(t => { const st = calClassTime(t.time); return calIsPractical(st) ? st : null; }).filter(Boolean).join(', ');
        const supText = tidesList.filter(t => t.type==='pleamar').map(t => { const st = calSupTime(t.time); return calIsPractical(st) ? st : null; }).filter(Boolean).join(', ');
        const dayCourses = courses.filter(c => c.date === dateStr);
        const antolin = dayCourses.filter(c => c.location==='san_antolin').sort((a,b)=>a.time.localeCompare(b.time)).map(c=>c.time).join(', ');
        const niembro = dayCourses.filter(c => c.location==='ria_niembro').sort((a,b)=>a.time.localeCompare(b.time)).map(c=>c.time).join(', ');
        const martin  = dayCourses.filter(c => c.location==='san_martin').sort((a,b)=>a.time.localeCompare(b.time)).map(c=>c.time).join(', ');
        wsData.push([`${CAL_DAYS[dt.getUTCDay()]} ${String(dt.getUTCDate()).padStart(2,'0')}`, coef, tidesText, classText, supText, antolin, niembro, martin]);
      });
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch:8 },{ wch:6 },{ wch:32 },{ wch:22 },{ wch:14 },{ wch:16 },{ wch:16 },{ wch:16 }];
    ws['!views'] = [{ state:'frozen', xSplit:1, ySplit:2 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Calendario');
    XLSX.writeFile(wb, 'calendario_surfskull.xlsx');
  };

  const getTide = (date) => tideData[date] || null;

  const handleCalTimeSave = useCallback(async (course, newTime) => {
    if (!newTime || newTime === course.time) return;
    setCalSaving(prev => ({ ...prev, [course.id]: true }));
    try {
      await updateCourse(course.id, { date: course.date, time: newTime, location: course.location, monitors: course.monitors || [], observations: course.observations || '', is_skate: course.is_skate || false, course_category: course.course_category || '' });
      setCourses(prev => prev.map(c => c.id === course.id ? { ...c, time: newTime } : c));
      toast.success(`Actualizado a ${newTime}`);
    } catch { toast.error('Error al actualizar'); }
    finally { setCalSaving(prev => ({ ...prev, [course.id]: false })); }
  }, []);

  const handleCalCreate = useCallback(async (date, location, suggestedTime) => {
    const key = `${date}_${location}`;
    setCalSaving(prev => ({ ...prev, [key]: true }));
    try {
      const { data } = await createCourse({ date, time: suggestedTime, location, monitors: [], observations: '', is_skate: false, course_category: location === 'ria_niembro' ? 'sup' : 'surf' });
      setCourses(prev => [...prev, data]);
      toast.success(`Curso creado ${suggestedTime}`);
    } catch { toast.error('Error al crear curso'); }
    finally { setCalSaving(prev => ({ ...prev, [key]: false })); }
  }, []);

  const toLocalTime = useCallback((timeStr, dateStr) => {
    const [h, m] = timeStr.split(':').map(Number);
    const lh = (h + getSpainOffset(dateStr)) % 24;
    return `${String(lh).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }, []);

  return (
    <div className="px-4 md:px-6 pb-4 md:pb-6 space-y-6" data-testid="schedule-page">
      <div className="sticky top-0 z-20 bg-[#FCFBF9] -mx-4 px-4 md:-mx-6 md:px-6 pt-4 md:pt-8 pb-3 space-y-3 border-b border-[#E5E0D8]">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>{monitorFilter ? `Programacion - ${monitorFilter}` : 'Programación'}</h1>
            <p className="text-sm text-[#8A9BA8] mt-1">{futureDates.length} programados · {pastDates.length} en historial</p>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild><Button variant="outline" size="icon" className={`border-[#E5E0D8] h-9 w-9 ${calendarDate ? 'bg-[#4A6D7C] text-white hover:bg-[#3A5663]' : 'text-[#4A5D6C]'}`} data-testid="schedule-calendar-btn"><CalendarDays className="w-4 h-4" /></Button></PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={calendarDate} month={calendarMonth} onMonthChange={setCalendarMonth} onSelect={(d) => { setCalendarDate(d); if (d) setCalendarMonth(d); setCalendarOpen(false); }} /></PopoverContent>
            </Popover>
            {calendarDate && <Button variant="ghost" size="sm" className="h-7 text-xs text-[#8A9BA8]" onClick={() => { setCalendarDate(null); setCalendarMonth(new Date()); }}>✕ {calendarDate.toLocaleDateString('es-ES')}</Button>}
            <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#8A9BA8]" /><Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 border-[#E5E0D8] w-24 sm:w-44 h-9" data-testid="schedule-search" /></div>
            {!monitorFilter && tab !== 'calendario' && tab !== 'windy' && tab !== 'swell' && tab !== 'webcam' && <Button variant="outline" onClick={handleExportExcel} className="border-[#E5E0D8] text-[#4A5D6C]"><Download className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Excel</span></Button>}
            {!monitorFilter && tab !== 'calendario' && tab !== 'windy' && tab !== 'swell' && tab !== 'webcam' && <Button variant="outline" onClick={handleExportPDF} className="border-[#E5E0D8] text-[#4A5D6C]"><Download className="w-4 h-4 mr-1" /><span className="hidden sm:inline">PDF</span></Button>}
            {!monitorFilter && tab === 'calendario' && <Button variant="outline" onClick={handleExportCalendarExcel} className="border-[#E5E0D8] text-[#4A5D6C]"><Download className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Excel</span></Button>}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-1">
          <div className="flex gap-1 bg-[#F5F2ED] p-1 rounded-lg">
            <button onClick={() => setTab('proximos')}
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'proximos' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
              <span className="sm:hidden">Próx.</span>
              <span className="hidden sm:inline">Programación</span>
              <span className="hidden sm:inline ml-1 text-xs bg-[#4A6D7C] text-white rounded-full px-1.5 py-0.5">{futureDates.length}</span>
            </button>
            <button onClick={() => setTab('historial')}
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'historial' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
              <span className="sm:hidden">Hist.</span>
              <span className="hidden sm:inline">Historial</span>
              <span className="hidden sm:inline ml-1 text-xs bg-[#8A9BA8] text-white rounded-full px-1.5 py-0.5">{pastDates.length}</span>
            </button>
            <button onClick={() => setTab('mareas')}
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'mareas' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
              Mareas
            </button>
            <button onClick={() => setTab('calendario')}
              className={`hidden sm:block px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'calendario' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
              Calendario
            </button>
            <button onClick={() => setTab('windy')}
              className={`hidden sm:block px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'windy' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
              Windy
            </button>
            <button onClick={() => setTab('swell')}
              className={`hidden sm:block px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'swell' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
              Swell
            </button>
            <button onClick={() => setTab('webcam')}
              className={`hidden sm:block px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'webcam' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
              Web Cam
            </button>
          </div>
          <div className="sm:hidden bg-[#F5F2ED] p-1 rounded-lg flex gap-1">
            <button onClick={() => setTab('calendario')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'calendario' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
              Calendario
            </button>
            <button onClick={() => setTab('windy')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'windy' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
              Windy
            </button>
            <button onClick={() => setTab('swell')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'swell' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
              Swell
            </button>
            <button onClick={() => setTab('webcam')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'webcam' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}>
              Web Cam
            </button>
          </div>
        </div>
      </div>

      {tab === 'mareas' && (
        <div className="space-y-8 py-4">
          {Array.from({ length: 7 }, (_, i) => {
            const [y, mo, d] = todayStr.split('-').map(Number);
            const dt = new Date(Date.UTC(y, mo - 1, d + i));
            const dateStr = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth()+1).padStart(2,'0')}-${String(dt.getUTCDate()).padStart(2,'0')}`;
            const tide = tideData[dateStr];
            if (!tide) return null;
            const off = getSpainOffset(dateStr);
            const dayLabel = i === 0 ? 'Hoy' : i === 1 ? 'Mañana' : (() => {
              const dn = new Date(dateStr + 'T12:00:00Z');
              const days = ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'];
              return days[dn.getUTCDay()];
            })();
            const [year, month, day2] = dateStr.split('-');
            const months = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
            const dateLabel = `${parseInt(day2, 10)} de ${months[parseInt(month,10)-1]}`;
            const allTides = tide.tides || [...(tide.high_tides || []), ...(tide.low_tides || [])];
            const sorted = [...allTides].sort((a, b) => a.time.localeCompare(b.time));
            return (
              <div key={dateStr} className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden">
                <div className="px-6 pt-4 pb-2 border-b border-[#E5E0D8] flex items-center gap-3">
                  <span className="text-base font-bold text-[#1A2B3C]">{dayLabel}</span>
                  <span className="text-sm text-[#8A9BA8]">{dateLabel}</span>
                  {tide.coefficient != null && (
                    <span className="ml-auto text-xs bg-[#F5F2ED] px-2 py-0.5 rounded text-[#4A6D7C] font-semibold">Coef. {fmtCoef(tide.coefficient)}</span>
                  )}
                </div>
                <div className="p-4 flex flex-col md:flex-row gap-4 items-start">
                  <div className="flex-1 min-w-0">
                    <TideChart tideInfo={tide} date={dateStr} location="Llanes" />
                  </div>
                  <div className="md:w-48 flex-shrink-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#8A9BA8] mb-2">Hora España</div>
                    <div className="space-y-1.5">
                      {sorted.map((t, idx) => {
                        const lh = (parseInt(t.time.split(':')[0], 10) + off) % 24;
                        const localTime = `${String(lh).padStart(2,'0')}:${t.time.split(':')[1]}`;
                        const isPleamar = t.type === 'pleamar';
                        return (
                          <div key={idx} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${isPleamar ? 'bg-[#81B29A]/10' : 'bg-[#E07A5F]/10'}`}>
                            <span className={`font-semibold ${isPleamar ? 'text-[#81B29A]' : 'text-[#E07A5F]'}`}>
                              {isPleamar ? '↑ Pleamar' : '↓ Bajamar'}
                            </span>
                            <div className="text-right">
                              <div className="font-mono text-[#1A2B3C]">{localTime}</div>
                              <div className="text-xs text-[#8A9BA8]">{t.height}m</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          }).filter(Boolean)}
          {!Object.keys(tideData).some(k => k >= todayStr) && (
            <div className="text-center py-16 text-[#8A9BA8]">
              <p>No hay datos de mareas disponibles.</p>
              <p className="text-sm mt-1">Importa las mareas en Ajustes → Mareas (PDF).</p>
            </div>
          )}
        </div>
      )}
      {tab === 'calendario' && (
        <div className="overflow-x-auto w-full" style={{ WebkitOverflowScrolling: 'touch' }}>
          <table className="w-full text-xs border-collapse" style={{ minWidth: 980 }}>
            <tbody>
              {['06', '07', '08', '09'].filter(mo => mo >= todayStr.substring(5, 7)).flatMap(mo => {
                const days = Object.keys(tideData)
                  .filter(d => d >= todayStr && d <= '2026-09-30' && d.startsWith('2026-' + mo))
                  .sort();
                if (!days.length) return [];
                return [
                  <tr key={`mh-${mo}`}><td colSpan={8} className="bg-[#1A2B3C] text-white font-bold text-sm px-3 py-2 tracking-wider" style={{ position:'sticky', left:0, zIndex:3 }}>{CAL_MONTHS[mo]} 2026</td></tr>,
                  <tr key={`ch-${mo}`} className="bg-[#F5F2ED] text-[#8A9BA8] uppercase font-semibold tracking-wide" style={{ fontSize: 9 }}>
                    <td className="px-2 py-1.5 w-16" style={{ position:'sticky', left:0, background:'#F5F2ED', zIndex:2 }}>Día</td>
                    <td className="px-2 py-1.5 w-10 text-center">Coef</td>
                    <td className="px-2 py-1.5 w-44">Mareas</td>
                    <td className="px-2 py-1.5 w-36">Clases sugeridas</td>
                    <td className="px-2 py-1.5 w-24">SUP sugerido</td>
                    <td className="px-2 py-1.5">S. Antolín</td>
                    <td className="px-2 py-1.5">Ría Niembro</td>
                    <td className="px-2 py-1.5">S. Martín</td>
                  </tr>,
                  ...days.map(dateStr => {
                    const dt = new Date(dateStr + 'T12:00:00Z');
                    const tide = tideData[dateStr];
                    const tidesList = tide?.tides || [];
                    const coef = tide?.coefficient != null ? Math.round(tide.coefficient < 2 ? tide.coefficient * 100 : tide.coefficient) : '';
                    const isToday = dateStr === todayStr;
                    const isWeekend = dt.getUTCDay() === 0 || dt.getUTCDay() === 6;

                    const classTimes = tidesList.map(t => {
                      const st = calClassTime(t.time);
                      if (!calIsPractical(st)) return null;
                      return { time: st, color: calClassColor(st), tipo: t.type === 'pleamar' ? 'P↑' : 'B↓', local: calLocal(t.time) };
                    }).filter(Boolean);

                    const supTimes = tidesList.filter(t => t.type === 'pleamar').map(t => {
                      const st = calSupTime(t.time);
                      if (!calIsPractical(st)) return null;
                      return { time: st, color: calSupColor(st), local: calLocal(t.time) };
                    }).filter(Boolean);

                    const dayCourses = courses.filter(c => c.date === dateStr && (!monitorFilter || (c.monitors || []).some(m => m.name && m.name.toLowerCase() === monitorFilter.toLowerCase())));
                    const antolin = dayCourses.filter(c => c.location === 'san_antolin').sort((a, b) => a.time.localeCompare(b.time));
                    const niembro = dayCourses.filter(c => c.location === 'ria_niembro').sort((a, b) => a.time.localeCompare(b.time));
                    const martin  = dayCourses.filter(c => c.location === 'san_martin').sort((a, b) => a.time.localeCompare(b.time));

                    const isBeforeJuly6 = dateStr < '2026-07-06';
                    const antolinSugg = isBeforeJuly6 ? [] : classTimes.filter(ct => ct.color === 'green' && !antolin.some(c => c.time === ct.time));
                    const niembroSugg = isBeforeJuly6 ? [] : supTimes.filter(st => st.color === 'green' && !niembro.some(c => c.time === st.time));

                    const rowBg = isToday ? 'bg-[#EFF6FF]' : isWeekend ? 'bg-[#FAFAF8]' : 'bg-white';
                    const rowBgVal = isToday ? '#EFF6FF' : isWeekend ? '#FAFAF8' : '#ffffff';
                    const borderLeft = isToday ? 'border-l-2 border-l-[#4A6D7C]' : '';

                    const CourseCell = ({ existing, suggested, loc, sugColor }) => {
                      const newKey = `new_${dateStr}_${loc}`;
                      if (monitorFilter) {
                        return (
                          <div className="flex flex-col gap-0.5">
                            {existing.map(course => (
                              <span key={course.id} className="h-6 inline-flex items-center px-1.5 rounded bg-[#81B29A] text-white text-xs font-mono w-20">
                                {course.time}
                              </span>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <div className="flex flex-col gap-0.5">
                          {existing.map(course => (
                            <input key={course.id} type="time" defaultValue={course.time}
                              className={`h-6 rounded border text-xs px-1 font-mono w-20 ${calSaving[course.id] ? 'border-[#2E6B4F] bg-[#2E6B4F] text-white' : 'border-[#81B29A] bg-[#81B29A] text-white'}`}
                              onBlur={e => handleCalTimeSave(course, e.target.value)}
                            />
                          ))}
                          {suggested && suggested.map((sg, i) => (
                            <button key={i} onClick={() => handleCalCreate(dateStr, loc, sg.time)}
                              disabled={!!calSaving[`${dateStr}_${loc}`]}
                              className="h-6 px-1.5 rounded border border-dashed text-xs hover:bg-opacity-10 whitespace-nowrap disabled:opacity-50"
                              style={{ borderColor: sugColor, color: sugColor }}>
                              +{sg.time}
                            </button>
                          ))}
                          <div className="flex gap-0.5 mt-0.5">
                            <input type="time"
                              value={calEditValues[newKey] || ''}
                              onChange={e => setCalEditValues(prev => ({ ...prev, [newKey]: e.target.value }))}
                              className="h-6 rounded border border-dashed border-[#C0A89A] text-xs px-1 font-mono w-20"
                            />
                            <button
                              onClick={() => {
                                const t = calEditValues[newKey];
                                if (t) { handleCalCreate(dateStr, loc, t); setCalEditValues(prev => ({ ...prev, [newKey]: '' })); }
                              }}
                              disabled={!calEditValues[newKey] || !!calSaving[`${dateStr}_${loc}`]}
                              className="h-6 w-6 rounded border border-dashed border-[#C0A89A] text-[#C0A89A] text-xs hover:bg-[#C0A89A]/10 disabled:opacity-30 flex items-center justify-center flex-shrink-0">
                              +
                            </button>
                          </div>
                        </div>
                      );
                    };

                    return (
                      <tr key={dateStr} className={`${rowBg} ${borderLeft} border-b border-[#E5E0D8]`}>
                        <td className="px-2 py-1.5 font-semibold text-[#1A2B3C] whitespace-nowrap align-top" style={{ position:'sticky', left:0, background:rowBgVal, zIndex:1 }}>
                          {CAL_DAYS[dt.getUTCDay()]} {String(dt.getUTCDate()).padStart(2,'0')}
                        </td>
                        <td className="px-2 py-1.5 text-center font-bold align-top" style={{ color: coef >= 80 ? '#2E6B4F' : coef >= 50 ? '#B07A2A' : '#9A4A4A' }}>{coef}</td>
                        <td className="px-2 py-1.5 align-top">
                          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                            {tidesList.map((t, i) => (
                              <span key={i} style={{ color: t.type === 'pleamar' ? '#2E6B9A' : '#8A9BA8' }} className="font-medium whitespace-nowrap">
                                {calLocal(t.time)}{t.type === 'pleamar' ? '↑' : '↓'}{t.height}m
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                            {classTimes.map((ct, i) => (
                              <span key={i} className="font-bold whitespace-nowrap" style={{ color: CAL_COLOR[ct.color], textDecoration: 'underline', textUnderlineOffset: 2 }}>
                                {ct.time} <span className="opacity-60 font-normal" style={{ fontSize: 9 }}>{ct.tipo}{ct.local}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                            {supTimes.map((st, i) => (
                              <span key={i} className="font-bold whitespace-nowrap" style={{ color: CAL_COLOR[st.color], textDecoration: 'underline', textUnderlineOffset: 2 }}>
                                {st.time} <span className="opacity-60 font-normal" style={{ fontSize: 9 }}>P↑{st.local}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <CourseCell existing={antolin} suggested={antolinSugg} loc="san_antolin" sugColor="#81B29A" />
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <CourseCell existing={niembro} suggested={niembroSugg} loc="ria_niembro" sugColor="#4A6D7C" />
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <CourseCell existing={martin} suggested={null} loc="san_martin" sugColor="#C0A89A" />
                        </td>
                      </tr>
                    );
                  })
                ];
              })}
            </tbody>
          </table>
          {!Object.keys(tideData).some(k => k >= todayStr) && (
            <div className="text-center py-16 text-[#8A9BA8]">
              <p>No hay datos de mareas para el calendario.</p>
            </div>
          )}
        </div>
      )}

      {tab === 'windy' && (
        <div className="w-full rounded-xl overflow-hidden border border-[#E5E0D8]" style={{ height: 'calc(100vh - 220px)', minHeight: 400 }}>
          <iframe
            src="https://embed.windy.com/embed2.html?lat=43.43&lon=-4.77&detailLat=43.43&detailLon=-4.77&width=650&height=450&zoom=11&level=surface&overlay=waves&product=ecmwf&menu=&message=true&marker=&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=default&metricTemp=default&radarRange=-1"
            width="100%"
            height="100%"
            frameBorder="0"
            title="Windy - Previsión olas y viento"
            allowFullScreen
          />
        </div>
      )}

      {tab === 'swell' && (
        <div className="w-full rounded-xl overflow-hidden border border-[#E5E0D8]" style={{ height: 'calc(100vh - 220px)', minHeight: 400 }}>
          <iframe
            src="https://www.todosurf.com/prevision/playa-de-san-antolin-spot94.htm"
            width="100%"
            height="100%"
            frameBorder="0"
            title="Swell - Playa de San Antolín"
            allowFullScreen
          />
        </div>
      )}

      {tab === 'webcam' && (
        <div className="w-full rounded-xl overflow-hidden border border-[#E5E0D8]" style={{ height: 'calc(100vh - 220px)', minHeight: 400 }}>
          <iframe
            src="https://rtsp.me/embed/5eYFHiYz/"
            width="100%"
            height="100%"
            frameBorder="0"
            title="Web Cam - Playa de San Antolín"
            allowFullScreen
          />
        </div>
      )}

      {tab !== 'mareas' && tab !== 'calendario' && tab !== 'windy' && tab !== 'swell' && tab !== 'webcam' && (loading ? <div className="skeleton-pulse h-40 rounded-lg" /> : displayDates.length === 0 ? (
        <div className="text-center py-16 text-[#8A9BA8]"><CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-40" />{tab === 'proximos' ? 'No hay cursos programados' : 'No hay cursos en el historial'}</div>
      ) : displayDates.map(date => {
        const dayCourses = [...grouped[date]].sort((a, b) => a.time.localeCompare(b.time));
        const tide = getTide(date);
        const courseChartData = dayCourses.map((c, i) => ({
          time: c.time,
          label: `${getLoc(c.location)} ${c.time}`,
          color: c.color || COURSE_COLORS[i % COURSE_COLORS.length],
        }));
        return (
          <div key={date} className="space-y-3">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-[#1A2B3C]">{fmtDate(date)}</h2>
              <span className="text-xs bg-[#4A6D7C]/10 text-[#4A6D7C] px-2 py-0.5 rounded-full">{dayCourses.length} curso{dayCourses.length > 1 ? 's' : ''}</span>
            </div>
            {tide && <TideChart tideInfo={tide} date={date} courses={courseChartData} location="Llanes" windData={windByDate[date]} marineData={marineByDate[date]} />}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {dayCourses.map((course, courseIdx) => {
                const courseColor = course.color || COURSE_COLORS[courseIdx % COURSE_COLORS.length];
                const namedStudents = [];
                const bulkReservations = [];
                (course.reservations || []).forEach(r => {
                  const named = (r.students || []).filter(s => s.name?.trim());
                  const numSt = r.num_students || 0;
                  if (numSt > 0 && named.length < numSt) {
                    bulkReservations.push(r);
                  } else if (named.length > 0) {
                    named.forEach(st => namedStudents.push({ name: st.name, courseId: course.id }));
                  } else {
                    namedStudents.push({ name: r.reservation_name, courseId: course.id });
                  }
                });
                const totalCount = namedStudents.length + bulkReservations.reduce((s, r) => s + (r.num_students || 0), 0);
                return (
                  <Card key={course.id} className="border-[#E5E0D8] bg-white overflow-hidden" data-testid={`schedule-card-${course.id}`}>
                    <div className="h-1" style={{ backgroundColor: courseColor }} />
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center flex-wrap gap-2">
                            <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: courseColor }} />
                            <span className="text-sm font-bold text-[#4A6D7C]">{getLoc(course.location)}</span>
                            <span className="text-sm text-[#8A9BA8]">{course.time}</span>
                          </div>
                          <div className="text-xs text-[#8A9BA8] mt-1">
                            Monitores: <span className="font-medium text-[#1A2B3C]">{(course.monitors || []).map(m => m.name).join(', ') || '-'}</span>
                          </div>
                        </div>
                        {tide && (
                          <div className="text-right text-[10px] leading-tight bg-[#F5F2ED] rounded-md px-2 py-1.5 min-w-[120px]">
                            <div className="flex items-center gap-1 mb-0.5"><Anchor className="w-3 h-3 text-[#4A6D7C]" /><span className="font-semibold text-[#4A6D7C]">Hora España</span></div>
                            {tide.coefficient != null && <div className="text-[#1A2B3C] font-semibold">Coef: {fmtCoef(tide.coefficient)}</div>}
                            {(() => {
                              const all = (tide.tides || [...(tide.high_tides || []), ...(tide.low_tides || [])]);
                              const pleamares = all.filter(t => t.type === 'pleamar');
                              const bajamares = all.filter(t => t.type === 'bajamar');
                              if (!pleamares.length && !bajamares.length) {
                                const sorted = [...all].map(t => ({...t, h: parseFloat(t.height)}));
                                const heights = sorted.map(p => p.h).sort((a, b) => a - b);
                                const med = heights[Math.floor(heights.length / 2)];
                                return (<>
                                  {sorted.filter(p => p.h >= med).map((t, i) => <div key={`h${i}`} className="text-[#81B29A]">Pleamar: {toLocalTime(t.time, date)} ({t.height}m)</div>)}
                                  {sorted.filter(p => p.h < med).map((t, i) => <div key={`l${i}`} className="text-[#E07A5F]">Bajamar: {toLocalTime(t.time, date)} ({t.height}m)</div>)}
                                </>);
                              }
                              return (<>
                                {pleamares.map((t, i) => <div key={`h${i}`} className="text-[#81B29A]">Pleamar: {toLocalTime(t.time, date)} ({t.height}m)</div>)}
                                {bajamares.map((t, i) => <div key={`l${i}`} className="text-[#E07A5F]">Bajamar: {toLocalTime(t.time, date)} ({t.height}m)</div>)}
                              </>);
                            })()}
                          </div>
                        )}
                      </div>
                      <div className="border-t border-[#E5E0D8] pt-2">
                        <div className="text-xs font-semibold text-[#8A9BA8] uppercase tracking-wider mb-1">Alumnos ({totalCount})</div>
                        {totalCount === 0 ? (
                          <p className="text-xs text-[#8A9BA8] italic">Sin alumnos registrados</p>
                        ) : (
                          <div className="space-y-1">
                            {namedStudents.map((st, idx) => {
                              const key = `${st.courseId}_${st.name}`;
                              const attended = !!attendance[key];
                              return (
                                <div key={`n${idx}`} className="flex items-center justify-between py-0.5">
                                  <span className="text-sm text-[#1A2B3C]">{st.name}</span>
                                  <button onClick={() => toggleAttendance(st.courseId, st.name)} className="transition-colors" data-testid={`attendance-${st.courseId}-${idx}`}>
                                    {attended ? <CheckCircle className="w-5 h-5 text-[#81B29A]" /> : <Circle className="w-5 h-5 text-[#D1D5DB]" />}
                                  </button>
                                </div>
                              );
                            })}
                            {bulkReservations.map((r) => (
                              <BulkAttendeeRow key={r.id} reservation={r} />
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      }))}
    </div>
  );
}
